// M10 不等式與線性規劃 - 主要聚合文件
// Inequalities and Linear Programming - Main Aggregation File
// 系統化設計，準備後續 Supabase 數據遷移

import {
  M10_TOPICS_01_TO_10,
  M10_PROBLEMS_01_TO_10
} from './M10_Topics_01_to_10.js';

import {
  M10_TOPICS_11_TO_20,
  M10_PROBLEMS_11_TO_20
} from './M10_Topics_11_to_20.js';

import {
  M10_TOPICS_21_TO_26,
  M10_PROBLEMS_21_TO_26
} from './M10_Topics_21_to_26.js';

// M10 模組基本信息
export const M10_MODULE_INFO = {
  id: 'M10',
  name: '不等式與線性規劃',
  description: '全面掌握不等式證明技巧、線性規劃求解方法與實際應用',
  category: 'advanced',
  difficulty: 'intermediate',
  estimatedTime: '25-35小時',
  totalQuestions: 130,
  totalTopics: 26,
  questionsPerTopic: 5,
  partsPerQuestion: 3,
  difficultyDistribution: {
    basic: 35,
    intermediate: 65,
    advanced: 30
  },
  topicCategories: {
    inequalities: {
      name: '不等式理論',
      topics: ['AM-GM不等式', 'AM-GM不等式應用', '不等式證明', '代換技巧', '平方處理', '有理不等式', '完全平方技巧', '無理不等式', '絕對值不等式', '複合不等式'],
      count: 10
    },
    analysis: {
      name: '分析方法',
      topics: ['根的分佈', '區間測試', '排序不等式', '幾何法', '極值分析', '極值評估', '解集交集', '數線分析', '臨界點分析'],
      count: 9
    },
    linearProgramming: {
      name: '線性規劃',
      topics: ['可行解頂點', '頂點評估', '圖解法', '線性規劃求解', '整數規劃'],
      count: 5
    },
    advanced: {
      name: '高級技巧',
      topics: ['定義域考慮', '拉格朗日乘數'],
      count: 2
    }
  },
  prerequisites: [
    '基礎代數運算',
    '二次函數性質',
    '函數圖像分析',
    '基礎微積分概念'
  ],
  learningObjectives: [
    '掌握各種不等式的證明方法',
    '熟練運用AM-GM不等式及其應用',
    '理解線性規劃的基本理論和求解方法',
    '能夠分析和解決實際優化問題',
    '掌握約束優化的基本技巧'
  ],
  realWorldApplications: [
    '經濟學中的資源配置問題',
    '工程優化設計',
    '投資組合優化',
    '生產計劃制定',
    '網絡流量優化'
  ],
  dataStructure: {
    topics: 'Array of topic objects with id, name, description, category, difficulty',
    problems: 'Array of problem objects with multi-part structure (parts a, b, c)',
    solutions: 'Detailed step-by-step solutions with mathematical expressions',
    hints: 'Learning hints for each problem part',
    metadata: 'Comprehensive module information for system integration'
  },
  supabaseSchema: {
    tableName: 'm10_inequalities_linear_programming',
    primaryKey: 'id',
    foreignKeys: ['topicId', 'moduleId'],
    jsonFields: ['parts', 'solution', 'metadata'],
    indexes: ['topicId', 'difficulty', 'type'],
    migrationReady: true
  }
};

// 聚合所有主題
export const M10_TOPICS = [
  ...M10_TOPICS_01_TO_10,
  ...M10_TOPICS_11_TO_20,
  ...M10_TOPICS_21_TO_26
];

// 聚合所有問題數據
export const M10_PROBLEMS_DATA = [
  ...M10_PROBLEMS_01_TO_10,
  ...M10_PROBLEMS_11_TO_20,
  ...M10_PROBLEMS_21_TO_26
];

// 數據驗證和完整性檢查
export function validateM10Data() {
  const validation = {
    topicsCount: M10_TOPICS.length,
    problemsCount: M10_PROBLEMS_DATA.length,
    expectedTopics: 26,
    expectedProblems: 130,
    isValid: true,
    errors: []
  };

  // 檢查主題數量
  if (validation.topicsCount !== validation.expectedTopics) {
    validation.isValid = false;
    validation.errors.push(`主題數量不匹配: 期望 ${validation.expectedTopics}, 實際 ${validation.topicsCount}`);
  }

  // 檢查問題數量
  if (validation.problemsCount !== validation.expectedProblems) {
    validation.isValid = false;
    validation.errors.push(`問題數量不匹配: 期望 ${validation.expectedProblems}, 實際 ${validation.problemsCount}`);
  }

  // 檢查每個主題的問題數量
  M10_TOPICS.forEach(topic => {
    const topicProblems = M10_PROBLEMS_DATA.filter(problem => problem.topicId === topic.id);
    if (topicProblems.length !== 5) {
      validation.isValid = false;
      validation.errors.push(`主題 ${topic.id} 問題數量不正確: 期望 5, 實際 ${topicProblems.length}`);
    }
  });

  // 檢查問題結構完整性
  M10_PROBLEMS_DATA.forEach(problem => {
    if (!problem.parts || !problem.parts.a || !problem.parts.b || !problem.parts.c) {
      validation.isValid = false;
      validation.errors.push(`問題 ${problem.id} 缺少必要的部分 (a, b, c)`);
    }
  });

  return validation;
}

// 獲取所有M10問題
export function getAllM10Problems() {
  return M10_PROBLEMS_DATA;
}

// 根據主題ID獲取問題
export function getM10ProblemsByTopic(topicId) {
  return M10_PROBLEMS_DATA.filter(problem => problem.topicId === topicId);
}

// 根據難度獲取問題
export function getM10ProblemsByDifficulty(difficulty) {
  return M10_PROBLEMS_DATA.filter(problem => problem.difficulty === difficulty);
}

// 根據類別獲取主題
export function getM10TopicsByCategory(category) {
  return M10_TOPICS.filter(topic => topic.category === category);
}

// 獲取問題統計信息
export function getM10Statistics() {
  const stats = {
    totalTopics: M10_TOPICS.length,
    totalProblems: M10_PROBLEMS_DATA.length,
    difficultyBreakdown: {
      basic: M10_PROBLEMS_DATA.filter(p => p.difficulty === 'basic').length,
      intermediate: M10_PROBLEMS_DATA.filter(p => p.difficulty === 'intermediate').length,
      advanced: M10_PROBLEMS_DATA.filter(p => p.difficulty === 'advanced').length
    },
    categoryBreakdown: {}
  };

  // 計算各類別的統計
  M10_TOPICS.forEach(topic => {
    const category = topic.category;
    if (!stats.categoryBreakdown[category]) {
      stats.categoryBreakdown[category] = 0;
    }
    stats.categoryBreakdown[category]++;
  });

  return stats;
}

// 生成隨機練習集
export function generateM10RandomPracticeSet(count = 10, difficulty = null) {
  let availableProblems = M10_PROBLEMS_DATA;
  
  if (difficulty) {
    availableProblems = availableProblems.filter(p => p.difficulty === difficulty);
  }

  const shuffled = [...availableProblems].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, shuffled.length));
}

// 獲取學習路徑建議
export function getM10LearningPath() {
  return [
    {
      phase: 1,
      name: '基礎不等式',
      topics: ['M10_09', 'M10_12', 'M10_17', 'M10_22'],
      description: '從基本概念開始，掌握不等式的基本求解方法',
      estimatedTime: '6-8小時'
    },
    {
      phase: 2,
      name: '經典不等式理論',
      topics: ['M10_01', 'M10_02', 'M10_03', 'M10_13'],
      description: '學習AM-GM不等式和其他重要不等式',
      estimatedTime: '8-10小時'
    },
    {
      phase: 3,
      name: '解題技巧',
      topics: ['M10_04', 'M10_06', 'M10_08', 'M10_15', 'M10_16'],
      description: '掌握各種不等式求解的技巧和方法',
      estimatedTime: '6-8小時'
    },
    {
      phase: 4,
      name: '線性規劃基礎',
      topics: ['M10_21', 'M10_14', 'M10_23'],
      description: '學習線性規劃的基本理論和圖解法',
      estimatedTime: '4-6小時'
    },
    {
      phase: 5,
      name: '高級主題',
      topics: ['M10_05', 'M10_10', 'M10_18', 'M10_19', 'M10_25', 'M10_26'],
      description: '深入學習約束優化和高級分析方法',
      estimatedTime: '8-12小時'
    }
  ];
}

// Supabase 遷移準備
export function prepareM10ForSupabase() {
  return {
    moduleInfo: M10_MODULE_INFO,
    topics: M10_TOPICS.map(topic => ({
      ...topic,
      moduleId: 'M10',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    })),
    problems: M10_PROBLEMS_DATA.map(problem => ({
      ...problem,
      moduleId: 'M10',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      metadata: {
        hasMultipleParts: true,
        partsCount: 3,
        hasDetailedSolutions: true,
        hasHints: true,
        mathJaxRequired: true
      }
    })),
    migrationScript: `
      -- M10 不等式與線性規劃 數據遷移腳本
      -- 創建主表
      CREATE TABLE IF NOT EXISTS m10_topics (
        id VARCHAR(10) PRIMARY KEY,
        name VARCHAR(100) NOT NULL,
        description TEXT,
        category VARCHAR(50),
        difficulty VARCHAR(20),
        module_id VARCHAR(10) DEFAULT 'M10',
        created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
        updated_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
      );

      CREATE TABLE IF NOT EXISTS m10_problems (
        id VARCHAR(15) PRIMARY KEY,
        topic_id VARCHAR(10) REFERENCES m10_topics(id),
        type VARCHAR(20) DEFAULT 'multi_part',
        difficulty VARCHAR(20),
        question TEXT NOT NULL,
        parts JSONB NOT NULL,
        module_id VARCHAR(10) DEFAULT 'M10',
        metadata JSONB,
        created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
        updated_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
      );

      -- 創建索引
      CREATE INDEX idx_m10_problems_topic_id ON m10_problems(topic_id);
      CREATE INDEX idx_m10_problems_difficulty ON m10_problems(difficulty);
      CREATE INDEX idx_m10_problems_type ON m10_problems(type);
    `
  };
}

// 導出默認對象以保持兼容性
export default {
  moduleInfo: M10_MODULE_INFO,
  topics: M10_TOPICS,
  problems: M10_PROBLEMS_DATA,
  helpers: {
    validateM10Data,
    getAllM10Problems,
    getM10ProblemsByTopic,
    getM10ProblemsByDifficulty,
    getM10TopicsByCategory,
    getM10Statistics,
    generateM10RandomPracticeSet,
    getM10LearningPath,
    prepareM10ForSupabase
  }
}; 